// Recipe-step parallelism validator. Pure JS, no framework deps —
// imported by AIRecipe.jsx alongside bakingRecipeValidator and
// merged into the same diagnostics band.
//
// Detects recipe steps that bury "while X cooks, do Y" inside ONE
// step's prose. Per the user's standing rule (2026-05-21), those
// should be split into two steps — passive (start the long cook)
// + active (the prep work) — so the cook UI can reassure the user
// their background timer is safe and they should move forward into
// real work. Stacking parallel work onto one step ties the cook to
// the step and produces sequential execution by default.
//
// Sister to src/lib/bakingRecipeValidator.js. Same diagnostic shape
// so the AIRecipe band can render both with no rework:
//   { ok, issues, warnings }
// Each entry: { kind, severity, stepId, message, suggestion }.

// Pattern matches the prose-level "meanwhile" giveaways. Anchored
// on whole words so "white" doesn't match "while". The (cooks|...|
// rests) alternation grounds the "as the X cooks" form against an
// actual cooking verb so generic prose ("as the recipe progresses")
// doesn't trip.
const SPLIT_PROSE_RE = /\b(while|meanwhile|as the [\w\s]+ (cooks|simmers|boils|bakes|rests|roasts|braises|reduces)|during the|once the [\w\s]+ is going)\b/i;

export function findStepParallelismIssues(recipe) {
  const steps = recipe?.steps || [];
  const warnings = [];
  for (const step of steps) {
    const text = step?.instruction || "";
    if (!text) continue;
    if (SPLIT_PROSE_RE.test(text)) {
      warnings.push({
        kind: "should_be_split_into_steps",
        severity: "warning",
        stepId: step.id,
        message: `Step "${step.title || step.id}" buries "while X cooks, do Y" in one instruction.`,
        suggestion: "Split into two steps — passive (start the long-running cook) + active (the prep work). Cooks default to sequential when prose says \"meanwhile\"; splitting tells them to move on.",
      });
    }
  }
  // No hard issues today — every parallelism finding is a warning
  // the author can override. `ok` stays true so the diagnostics
  // band renders the gold "notes" treatment, not the red blocker.
  return { ok: true, issues: [], warnings };
}
